import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LabellerTimelinesReport,
  TimelineFeature,
} from "src/store/api/generatedApi";
import {
  TIMELINE_FEATURES,
  TimelineExclusionPeriodIds,
} from "../utils/timelineExclusions";

/** What an exclusion resolver reads, which a review-events response also carries. */
export type TimelineExclusionReport = LabellerTimelinesReport;

export interface TimelineExclusions {
  excludedPeriodIds: Set<number>;
  setExcludedPeriodIds: Dispatch<SetStateAction<Set<number>>>;
  materialExcludedPeriodIds: Set<number>;
  setMaterialExcludedPeriodIds: Dispatch<SetStateAction<Set<number>>>;
  timingExcludedPeriodIds: Set<number>;
  setTimingExcludedPeriodIds: Dispatch<SetStateAction<Set<number>>>;
  /**
   * Folds a resolved set of exclusions into the sets. Idempotent per period and feature, so
   * a refetch does not undo the reviewer's own toggles. A surface cannot know its exclusions
   * until it has fetched once, and the fetch is keyed on them, so the caller drives this from
   * an effect on its response. Pass only what a response for the arguments in force resolved
   * to — a lagging one carries another window's periods.
   */
  applyResolved: (resolved: TimelineExclusionPeriodIds | undefined) => void;
  /**
   * Whether a response for this window has been resolved, so the sets are the ones the
   * surface should merge with. Until it is, a surface has only its opening guess — no
   * exclusions at all — and should not present that as the merge.
   */
  resolved: boolean;
}

interface ExclusionState extends TimelineExclusionPeriodIds {
  windowKey: string;
  // Every `${feature}|${periodId}` a default has already been applied to, so a labeller
  // the reviewer puts back in view is not excluded again by the next fetch.
  appliedDefaults: Set<string>;
  resolved: boolean;
}

const initialState = (
  windowKey: string,
  inherited: TimelineExclusionPeriodIds | undefined
): ExclusionState => ({
  windowKey,
  all: new Set(inherited?.all),
  material: new Set(inherited?.material),
  timing: new Set(inherited?.timing),
  appliedDefaults: new Set(),
  resolved: false,
});

const withDefaultsApplied = (
  previous: ExclusionState,
  defaults: TimelineExclusionPeriodIds
): ExclusionState => {
  const unapplied = TIMELINE_FEATURES.map((feature) => ({
    feature,
    periodIds: [...defaults[feature]].filter(
      (periodId) => !previous.appliedDefaults.has(`${feature}|${periodId}`)
    ),
  })).filter(({ periodIds }) => periodIds.length > 0);

  if (unapplied.length === 0) return previous;

  return unapplied.reduce<ExclusionState>(
    (state, { feature, periodIds }) => ({
      ...state,
      [feature]: new Set([...state[feature], ...periodIds]),
      appliedDefaults: new Set([
        ...state.appliedDefaults,
        ...periodIds.map((periodId) => `${feature}|${periodId}`),
      ]),
    }),
    previous
  );
};

/**
 * The exclusion sets a review surface merges with: the defaults its fetch reports, applied
 * once each, plus whatever the reviewer has since toggled. `windowKey` identifies the time
 * window the sets belong to — changing it starts over, so a surface that moves to a new
 * window does not carry the previous window's exclusions.
 *
 * `inherited` seeds the sets before anything has been resolved, so a surface has something
 * better than nothing to open with.
 */
export const useTimelineExclusions = ({
  windowKey,
  inherited,
}: {
  windowKey: string;
  inherited?: TimelineExclusionPeriodIds;
}): TimelineExclusions => {
  const [state, setState] = useState<ExclusionState>(() =>
    initialState(windowKey, inherited)
  );

  // Read through a ref so a new window starts from the ancestor's sets as they stand at
  // that moment, without the ancestor's own refetches re-keying this surface.
  const inheritedRef = useRef(inherited);
  inheritedRef.current = inherited;

  const forWindow = useCallback(
    (candidate: ExclusionState): ExclusionState =>
      candidate.windowKey === windowKey
        ? candidate
        : initialState(windowKey, inheritedRef.current),
    [windowKey]
  );

  const current = useMemo(() => forWindow(state), [forWindow, state]);

  const applyResolved = useCallback(
    (resolvedExclusions: TimelineExclusionPeriodIds | undefined) => {
      if (!resolvedExclusions) return;
      setState((previous) => {
        const applied = withDefaultsApplied(
          forWindow(previous),
          resolvedExclusions
        );
        if (applied === previous && previous.resolved) return previous;
        return { ...applied, resolved: true };
      });
    },
    [forWindow]
  );

  const setters = useMemo(() => {
    const setFeature =
      (feature: TimelineFeature) => (action: SetStateAction<Set<number>>) => {
        setState((previous) => {
          const base = forWindow(previous);
          return {
            ...base,
            [feature]:
              typeof action === "function" ? action(base[feature]) : action,
          };
        });
      };
    return {
      all: setFeature("all"),
      material: setFeature("material"),
      timing: setFeature("timing"),
    };
  }, [forWindow]);

  return useMemo(
    () => ({
      excludedPeriodIds: current.all,
      setExcludedPeriodIds: setters.all,
      materialExcludedPeriodIds: current.material,
      setMaterialExcludedPeriodIds: setters.material,
      timingExcludedPeriodIds: current.timing,
      setTimingExcludedPeriodIds: setters.timing,
      applyResolved,
      resolved: current.resolved,
    }),
    [current, setters, applyResolved]
  );
};
